import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, DollarSign, Zap, Trophy,
  Lock, Unlock, Users, Smartphone, TrendingUp, Star,
  Check, ArrowRight, Shield, Brain,
} from 'lucide-react';

// ─── Server Onboarding / Trojan Horse ───────────────────────────────
// The app that servers download on their OWN phone.
// Free tier: personal tip tracking + AI scripts.
// Locked tier: live bounties + multiplayer leaderboard.
// The unlock? "Ask your manager to connect the POS."

type Step = 'hero' | 'free' | 'locked' | 'ask';

const ServerOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('hero');

  const renderHero = () => (
    <div className="flex flex-col items-center justify-center min-h-[80vh] text-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
        <DollarSign className="w-8 h-8 text-white" />
      </div>
      <h1 className="text-3xl font-black text-white mb-2">Bespoke</h1>
      <p className="text-gray-400 text-lg mb-8">Make more money. Every shift.</p>

      <div className="space-y-3 max-w-xs w-full mb-10">
        {[
          { icon: DollarSign, text: 'Track your real earnings per shift' },
          { icon: Brain, text: 'Get AI scripts that actually work' },
          { icon: Trophy, text: 'Compete for cash bounties' },
          { icon: TrendingUp, text: 'Watch yourself level up over time' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 text-left bg-white/5 rounded-lg px-4 py-3">
            <item.icon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <span className="text-sm text-gray-300">{item.text}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => setStep('free')}
        className="w-full max-w-xs px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-xl text-base hover:from-emerald-400 hover:to-cyan-400 transition-all active:scale-[0.98] shadow-lg shadow-emerald-500/20"
      >
        Get Started — It's Free
      </button>
      <p className="text-xs text-gray-600 mt-3">No sign-up fee. No manager needed. Just you and your phone.</p>
    </div>
  );

  const renderFree = () => (
    <div className="px-6 py-8">
      <h2 className="text-xl font-bold text-white mb-1">Your Free Toolkit</h2>
      <p className="text-sm text-gray-400 mb-6">Everything you need to start earning more. Right now. On your phone.</p>

      <div className="space-y-3 mb-8">
        {[
          {
            icon: DollarSign, title: 'Shift Earnings Tracker',
            desc: 'Log your tips after every shift. See your daily, weekly, and monthly trends. Know your real hourly rate.',
            free: true,
          },
          {
            icon: Brain, title: 'AI Cheat Codes',
            desc: 'Smart upsell scripts customized to your restaurant\'s menu. Updated daily based on what\'s working.',
            free: true,
          },
          {
            icon: Star, title: 'Personal Stats & Growth',
            desc: 'Track your upsell attempts, success rate, and earnings growth over time. Like Strava for your serving career.',
            free: true,
          },
          {
            icon: Smartphone, title: 'Pre-Shift Briefing',
            desc: 'Before you clock in: tonight\'s specials, what to push, and the scripts to use. 30-second prep.',
            free: true,
          },
        ].map((item, i) => (
          <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded-full">FREE</span>
                </div>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => setStep('locked')}
        className="w-full px-6 py-3 bg-white text-gray-900 font-bold rounded-xl text-sm hover:bg-gray-100 transition-all"
      >
        Continue
      </button>
    </div>
  );

  const renderLocked = () => (
    <div className="px-6 py-8">
      <h2 className="text-xl font-bold text-white mb-1">Unlock Multiplayer</h2>
      <p className="text-sm text-gray-400 mb-6">The free tools are powerful. But the real money is in the game.</p>

      <div className="space-y-3 mb-6">
        {[
          {
            icon: Zap, title: 'Live Flash Bounties',
            desc: '"The kitchen has extra halibut — first 2 servers to sell it get $10 instant bonus." Real cash, real-time.',
          },
          {
            icon: Trophy, title: 'Shift Leaderboard',
            desc: 'See who\'s winning RIGHT NOW. Friendly competition that pushes everyone to earn more.',
          },
          {
            icon: Users, title: 'Team Challenges',
            desc: 'Crew hits the team goal? Everyone gets a bonus. Makes the whole shift more fun.',
          },
          {
            icon: TrendingUp, title: 'Verified Earnings',
            desc: 'POS-verified data means your stats are real. Build a track record that follows you.',
          },
        ].map((item, i) => (
          <div key={i} className="bg-gray-900 border border-amber-500/20 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-2 right-2">
              <Lock className="w-4 h-4 text-amber-500/50" />
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-0.5">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Unlock className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-amber-300">How to unlock</h3>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Multiplayer features need your restaurant's POS connected (Toast, Square, etc.).
          This takes your manager 2 minutes. It's free for them too.
        </p>
        <p className="text-xs text-gray-500">
          Why? Because bounties and leaderboards need real sales data to verify who actually sold what.
          No gaming the system — everyone earns what they earn.
        </p>
      </div>

      <button
        onClick={() => setStep('ask')}
        className="w-full px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl text-sm hover:from-amber-400 hover:to-orange-400 transition-all active:scale-[0.98] shadow-lg shadow-amber-500/20"
      >
        Show me how to ask my manager
      </button>
      <button
        onClick={() => navigate('/restaurant/mockups/shift-live')}
        className="w-full mt-2 px-6 py-3 text-gray-500 text-sm hover:text-gray-300 transition-colors"
      >
        Skip for now — use the free tools
      </button>
    </div>
  );

  const renderAsk = () => (
    <div className="px-6 py-8">
      <h2 className="text-xl font-bold text-white mb-1">The 30-Second Ask</h2>
      <p className="text-sm text-gray-400 mb-6">Here's exactly what to say to your manager.</p>

      {/* The script */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 mb-6">
        <div className="text-sm text-gray-300 leading-relaxed space-y-3">
          <p><span className="text-amber-300 font-medium">You:</span> "Hey, I've been using this app that helps me upsell better — it gives me scripts and tracks my stats. It's actually working, I'm making more tips."</p>
          <p><span className="text-amber-300 font-medium">You:</span> "There's a feature that creates live upsell challenges during the shift — like bounties for selling specials. But it needs to connect to our POS to verify sales."</p>
          <p><span className="text-amber-300 font-medium">You:</span> "Would you be open to connecting it? It's free, takes 2 minutes, and it basically gets the whole staff to push specials without you having to ask."</p>
        </div>
      </div>

      {/* Why managers say yes */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-white mb-3">Why managers say yes</h3>
        <div className="space-y-2">
          {[
            'Staff actively pushes specials and high-margin items without being told',
            'Moves inventory the kitchen needs to clear (flash bounties)',
            'Zero cost — no hardware, no subscription for the restaurant',
            'Reduces turnover — servers making more money stick around longer',
            'Data on what\'s selling and what pitches work best',
          ].map((reason, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-gray-400">{reason}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Send to manager */}
      <button className="w-full px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-xl text-sm hover:from-emerald-400 hover:to-cyan-400 transition-all active:scale-[0.98] mb-3">
        Text this to my manager
      </button>
      <button className="w-full px-6 py-3 bg-gray-900 border border-gray-800 text-gray-300 font-medium rounded-xl text-sm hover:bg-gray-800 transition-all mb-3">
        Share the manager sign-up link
      </button>
      <button
        onClick={() => navigate('/restaurant/mockups/shift-live')}
        className="w-full px-6 py-3 text-gray-500 text-sm hover:text-gray-300 transition-colors"
      >
        I'll do it later — show me the app
      </button>

      <div className="mt-6 text-center">
        <p className="text-[11px] text-gray-600">
          87% of managers connect within 24 hours of being asked. Most say "why didn't we have this sooner?"
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Phone frame look */}
      <div className="max-w-sm mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2">
          <button onClick={() => {
            if (step === 'hero') navigate('/restaurant/mockups');
            else if (step === 'free') setStep('hero');
            else if (step === 'locked') setStep('free');
            else setStep('locked');
          }} className="text-gray-500 hover:text-gray-300 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-1">
            {(['hero', 'free', 'locked', 'ask'] as Step[]).map((s, i) => (
              <div key={s} className={`h-1 rounded-full transition-all ${
                s === step ? 'w-6 bg-emerald-400' : 'w-2 bg-gray-800'
              }`} />
            ))}
          </div>
          <div className="w-5" />
        </div>

        {step === 'hero' && renderHero()}
        {step === 'free' && renderFree()}
        {step === 'locked' && renderLocked()}
        {step === 'ask' && renderAsk()}
      </div>
    </div>
  );
};

export default ServerOnboarding;
